import { Link, useLocation } from "react-router-dom";
import { SoccerBall } from "@phosphor-icons/react";

/**
 * ChatFAB — Botón flotante Asesor IA.
 * Diseño: burbuja deportiva dorada con balón de fútbol (sin icono de robot).
 * - Mobile: icono solo, posicionado sobre la MobileNav.
 * - Desktop: muestra tooltip "Asesor IA" al hacer hover.
 */
export default function ChatFAB() {
  const { pathname } = useLocation();
  if (pathname === "/chat") return null;

  const isAdmin = pathname.startsWith("/admin");

  return (
    <>
      <Link
        to="/chat"
        data-testid="chat-fab"
        title="Asesor IA"
        className={`chat-fab ${isAdmin ? "chat-fab--admin" : "chat-fab--user"}`}
        aria-label="Abrir Asesor IA"
      >
        {/* Anillos sonar */}
        <span className="sonar-ring sonar-ring-1" />
        <span className="sonar-ring sonar-ring-2" />

        {/* Círculo principal — burbuja deportiva */}
        <span className="fab-circle">
          <SoccerBall weight="fill" className="fab-icon" />
          {/* Punto de "en línea" */}
          <span className="fab-online-dot" />
        </span>

        {/* Tooltip en hover (desktop) */}
        <span className="fab-tooltip">Asesor IA</span>
      </Link>

      <style>{`
        /* ── Contenedor ── */
        .chat-fab {
          position: fixed;
          right: 18px;
          z-index: 9999;
          display: flex;
          align-items: center;
          text-decoration: none;
          /* anillos necesitan position relative en el padre */
        }

        /* ── Posición mobile ── */
        .chat-fab--admin { bottom: 56px; }
        .chat-fab--user  { bottom: 52px; }

        /* ── Posición desktop ── */
        @media (min-width: 1024px) {
          .chat-fab--admin,
          .chat-fab--user { bottom: 22px; }
        }

        /* ── Círculo principal: burbuja deportiva ── */
        .fab-circle {
          position: relative;
          width: 54px;
          height: 54px;
          border-radius: 50%;
          background: linear-gradient(145deg, #F5D76E 0%, #D4AF37 45%, #9A7B1E 100%);
          box-shadow:
            0 0 0 2px rgba(212,175,55,0.35),
            0 6px 24px rgba(212,175,55,0.55),
            0 3px 8px rgba(0,0,0,0.6),
            inset 0 2px 4px rgba(255,255,255,0.35),
            inset 0 -3px 6px rgba(0,0,0,0.25);
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          transition: transform 0.2s ease, box-shadow 0.2s ease;
          animation: fab-float 3.5s ease-in-out infinite;
        }

        /* ── Balón de fútbol ── */
        .fab-icon {
          width: 30px;
          height: 30px;
          color: #1a1206;
          filter: drop-shadow(0 1px 2px rgba(0,0,0,0.4));
        }

        /* ── Punto verde "en línea" ── */
        .fab-online-dot {
          position: absolute;
          bottom: 2px;
          right: 2px;
          width: 13px;
          height: 13px;
          border-radius: 50%;
          background: #10B981;
          border: 2.5px solid #0d0d14;
          box-shadow: 0 0 8px rgba(16,185,129,0.8);
          animation: online-blink 2s ease-in-out infinite;
        }

        @keyframes online-blink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.65; }
        }

        /* ── Flotado suave (bobbing) ── */
        @keyframes fab-float {
          0%, 100% { transform: translateY(0);    }
          50%       { transform: translateY(-4px); }
        }

        /* ── Anillos sonar ── */
        .sonar-ring {
          position: absolute;
          border-radius: 50%;
          border: 2px solid rgba(212,175,55,0.55);
          pointer-events: none;
          /* centrar sobre el círculo */
          width: 54px;
          height: 54px;
          top: 0;
          left: 0;
          animation: sonar-expand 2.4s ease-out infinite;
        }
        .sonar-ring-2 {
          animation-delay: 1.2s;
          border-color: rgba(240,192,64,0.35);
        }

        @keyframes sonar-expand {
          0%   { transform: scale(1);   opacity: 0.8; }
          100% { transform: scale(2.4); opacity: 0;   }
        }

        /* ── Tooltip ── */
        .fab-tooltip {
          position: absolute;
          right: calc(100% + 10px);
          top: 50%;
          transform: translateY(-50%) translateX(6px);
          background: rgba(10,10,14,0.92);
          border: 1px solid rgba(212,160,23,0.35);
          color: #F0C040;
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.06em;
          white-space: nowrap;
          padding: 5px 10px;
          border-radius: 6px;
          pointer-events: none;
          opacity: 0;
          transition: opacity 0.2s ease, transform 0.2s ease;
          backdrop-filter: blur(8px);
        }

        /* Tooltip visible en hover (solo desktop) */
        @media (min-width: 1024px) {
          .chat-fab:hover .fab-tooltip {
            opacity: 1;
            transform: translateY(-50%) translateX(0);
          }
          .chat-fab:hover .fab-circle {
            transform: scale(1.1);
            box-shadow:
              0 0 0 3px rgba(212,160,23,0.3),
              0 6px 28px rgba(212,160,23,0.65),
              0 2px 8px rgba(0,0,0,0.6);
            animation: none; /* pausa el float en hover */
          }
        }

        /* ── Mobile: tap feedback ── */
        @media (max-width: 1023px) {
          .chat-fab:active .fab-circle {
            transform: scale(0.92);
          }
        }
      `}</style>
    </>
  );
}
